import { FormControlLabel, Grid, MenuItem, Skeleton, Stack, Switch, TextField, useTheme } from "@mui/material"
import { t } from "i18next"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CustomMap, IMarker } from "../../../partials/maps/custom-map"
import { usePolygonDraw } from "../../../partials/maps/use-polygon-draw"
import { MapsHelper } from "../../../partials/maps/maps-helper"
import { useCollectionGroupWizardContext } from "../context/collection-group-wizard-context"
import { CollectionGroupCollectionPointInfo } from "./collection-group-collection-point-info"
import { CollectionGroupCollectionPointsFilter } from "./collection-group-collection-points-filter"
import { CollectionGroupCollectionPointsTableDragNDrop } from "./collection-group-collection-points-table-drag-n-drop"
import { getCollectionPointsForMap_getCollectionPointsWithRegionID_entries } from "../../../../api/graphql/queries/types/getCollectionPointsForMap"
import { useQuery } from "@apollo/client"
import {
  GET_COLLECTION_POINTS_BY_IDS,
  GetCollectionPointsByIdsResult,
  GetCollectionPointsByIdsVars,
} from "../../../../api/graphql/queries/get-collection-points-by-ids"

export enum MapModeOption {
  Hand = "hand",
  Polygon = "polygon",
}

export const CollectionGroupReviewForm: React.FC = () => {
  const {
    collectionPointsForMap,
    formValues,
    loading,
    collectionPointsForMap: allCollectionPoints,
    setFormValues,
    orderOn,
    setOrderOn,
    mapUpdate, 
    setMapUpdate,
    setSelectedTableEntries,
    sortedCollectionPoints,
    startDepot, setStartDepot,
    endDepot, setEndDepot,
    departurePoints
  } = useCollectionGroupWizardContext()

  const selectedIdsAsStrings = (formValues?.collectionPointIds ?? []).map(String)

  const { data: selectedPointsData } = useQuery<GetCollectionPointsByIdsResult, GetCollectionPointsByIdsVars>(
    GET_COLLECTION_POINTS_BY_IDS,
    {
      variables: { ids: selectedIdsAsStrings },
      skip: selectedIdsAsStrings.length === 0,
    },
  )

  const theme = useTheme()
  const getMapHeight = useCallback(() => {
    const minHeight = 260
    const indicatorHeight = 100
    const headerHeight = 72
    const buttonWizardRow = 68
    const paddingsOffset = 36
    const height = window.innerHeight - indicatorHeight - headerHeight - buttonWizardRow - paddingsOffset
    return height > minHeight ? height : minHeight
  }, [])

  const [mapHeight, setMapHeight] = useState(getMapHeight())
  const [polygons, setPolygons] = useState<google.maps.Polygon[]>([])
  const [mode, setMode] = useState<MapModeOption.Hand | MapModeOption.Polygon>(MapModeOption.Hand)
  const [checkedMarkerIds, setCheckedMarkerIds] = useState<number[]>([])
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)

  const mapRef = useRef<google.maps.Map | null>(null)
  const buttonRefs = useRef<{
    hand?: HTMLButtonElement
    polygon?: HTMLButtonElement
    clearPolygons?: HTMLButtonElement
  }>({})

  const createButtons = (map: google.maps.Map | null) => {
    if (map) {
      map.controls[google.maps.ControlPosition.LEFT_TOP].clear()

      const controlDiv = document.createElement("div")
      controlDiv.style.display = "flex"
      controlDiv.style.flexDirection = "row"
      controlDiv.style.backgroundColor = "#fff"
      controlDiv.style.border = "2px solid #fff"
      controlDiv.style.borderRadius = "4px"
      controlDiv.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)"
      controlDiv.style.marginTop = "30px"
      controlDiv.style.marginLeft = "8px"
      controlDiv.style.padding = "5px"

      const createButton = (label: string, isActive: boolean, onClick: () => void) => {
        const button = document.createElement("button")
        button.innerHTML = label
        button.style.fontSize = "18px"
        button.style.filter = "grayscale(100%)"
        button.style.border = "none"
        button.style.background = isActive ? "#ccc" : "transparent"
        button.style.cursor = "pointer"
        button.style.margin = "0 5px"
        button.style.padding = "8px"
        button.style.borderRadius = "3px"
        button.onclick = onClick
        return button
      }

      const createClearButton = (label: string, onClick: () => void) => {
        const button = document.createElement("button")
        button.innerHTML = label
        button.style.fontSize = "18px"
        button.style.border = "none"
        button.style.background = "transparent"
        button.style.cursor = "pointer"
        button.style.margin = "0 5px"
        button.style.padding = "8px"
        button.style.borderRadius = "3px"
        button.style.display = polygons.length ? "block" : "none"
        button.style.color = "#666"
        button.onmouseover = () => {
          button.style.color = "#000"
        }
        button.onmouseout = () => {
          button.style.color = "#666"
        }
        button.onclick = onClick
        return button
      }

      buttonRefs.current.hand = createButton("✋🏿", mode === MapModeOption.Hand, () => setMode(MapModeOption.Hand))
      buttonRefs.current.polygon = createButton("⬟", mode === MapModeOption.Polygon, () =>
        setMode(MapModeOption.Polygon),
      )
      buttonRefs.current.clearPolygons = createClearButton(t("collection_points.clear"), clearPolygons)

      controlDiv.appendChild(buttonRefs.current.hand)
      controlDiv.appendChild(buttonRefs.current.polygon)
      controlDiv.appendChild(buttonRefs.current.clearPolygons)
      map.controls[google.maps.ControlPosition.LEFT_TOP].push(controlDiv)
    }
  }

  useEffect(() => {
    if (buttonRefs.current.hand && buttonRefs.current.polygon) {
      buttonRefs.current.hand.style.background = mode === MapModeOption.Hand ? "#ccc" : "transparent"
      buttonRefs.current.polygon.style.background = mode === MapModeOption.Polygon ? "#ccc" : "transparent"
      mapRef.current?.setOptions({ draggableCursor: mode === MapModeOption.Hand ? "grab" : "crosshair" })
    }
  }, [mode])


  useEffect(() => {    
    if (formValues?.startDepot?.id && setStartDepot) {
      setStartDepot(formValues.startDepot.id.toString());
    }

    if (formValues?.endDepot?.id && setEndDepot) {
      setEndDepot(formValues.endDepot.id.toString());
    }
  }, [formValues?.startDepot, formValues?.endDepot]);


  useEffect(() => {
    if (loading) return
    const materialIds = formValues?.materialIds ?? []
    const currentlySelectedCollectionPointIds = formValues?.collectionPointIds ?? []
    
    const collectionPoints = allCollectionPoints.filter((cp) =>
      currentlySelectedCollectionPointIds.includes(Number(cp.id)),
    )

    const collectionPointsForMaterials = collectionPoints.filter((cp) => {
      const collectionPointMaterialIds = cp.containers.flatMap((c) => c.material_id)
      return materialIds.some((id) => collectionPointMaterialIds.includes(id))
    })

    setFormValues({
      ...formValues,
      collectionPointIds: collectionPointsForMaterials.map((cp) => Number(cp.id)),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCollectionPoints, formValues?.materialIds, setFormValues, loading])

  useEffect(() => {
    const handleResize = () => {
      setMapHeight(getMapHeight())
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [getMapHeight])

  useEffect(() => {
    if (!selectedPointsData?.getCollectionPointsByIds) return

    if (setSelectedTableEntries) {
      setSelectedTableEntries(selectedPointsData.getCollectionPointsByIds as any)
    }
  }, [selectedPointsData?.getCollectionPointsByIds, setSelectedTableEntries])

  const markers = useMemo(() => {
    const markers = MapsHelper.getMarkersWithFilllevels(orderOn 
      ? sortedCollectionPoints?.map(c => allCollectionPoints?.find(p => +p.id === c)).filter((c): c is getCollectionPointsForMap_getCollectionPointsWithRegionID_entries => c != null)
      || []
    : allCollectionPoints?.filter(c => formValues?.collectionPointIds?.includes(+c.id)) || [], 100)
    
    const mapped = markers.map((marker, index) => {
      const isSelected = formValues?.collectionPointIds?.includes(marker.id)
      const color = isSelected ? theme.palette.primary.main.replace("#", "") : theme.palette.secondary.light.replace("#", "")
      return {
        ...marker,
        sequenceNumber: index + 1,
        label: `${index + 1}`,
        filllevel: orderOn ? index + 1: undefined,
        colorOverride: color,
      }
    })
    const depots = departurePoints?.filter(d => d.id === +startDepot || d.id === +endDepot).map(d => ({
      filllevel: undefined,
      sequenceNumber: 0,
      colorOverride: d.id === +startDepot ? "1aad03" : "ff0000",
      id: Number(d.id),
      label: `depot`,
      lat: d.latitude,
      lng: d.longitude,
    })) || [];

    return mapped.concat(depots)
  }, [collectionPointsForMap, formValues, theme, orderOn, sortedCollectionPoints, startDepot, endDepot, departurePoints])

  const renderCustomInfoWindow = (id: number) => {
    return <CollectionGroupCollectionPointInfo id={id} />
  }

  const fitMapBounds = (map: google.maps.Map) => {
    mapRef.current = map
    setMapInstance(map)
    createButtons(map)
    if (markers.length > 1) {
      const bounds = new google.maps.LatLngBounds()

      markers.forEach((marker) => {
        bounds.extend(new google.maps.LatLng(marker.lat, marker.lng))
      })

      map.fitBounds(bounds)
    } else if (markers.length === 1) {
      map.setCenter(new google.maps.LatLng(markers[0].lat, markers[0].lng))
      map.setZoom(15)
    }
  }

  const isMarkerInPolygon = useCallback(
    (marker: IMarker) => {
      return polygons.some((polygon) => {
        const googleMarker = new window.google.maps.LatLng(marker.lat, marker.lng)
        return window.google.maps.geometry.poly.containsLocation(googleMarker, polygon)
      })
    },
    [polygons],
  )

  const areEqual = (arr1: number[], arr2: number[]) =>
    arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index])

  const checkMarkersInsidePolygons = useCallback(() => {
    return markers.filter((marker) => isMarkerInPolygon(marker)).map((marker) => marker.id)
  }, [markers, isMarkerInPolygon])

  const updateCheckedMarkers = useCallback(
    (markersIds: number[]) => {
      const filteredFormValuesCollectionPointIds = formValues?.collectionPointIds?.filter(
        (id) => !checkedMarkerIds.includes(id) || markersIds.includes(id),
      )
      const newFormValuesCollectionPointIds = markersIds.filter(
        (id) => !filteredFormValuesCollectionPointIds?.includes(id),
      )
      setFormValues({
        ...formValues,
        collectionPointIds: [...(filteredFormValuesCollectionPointIds ?? []), ...newFormValuesCollectionPointIds],
      })
      setCheckedMarkerIds(markersIds)
    },
    [formValues, setFormValues, checkedMarkerIds, setCheckedMarkerIds],
  )

  useEffect(() => {
    if(setMapUpdate){
      setMapUpdate(!mapUpdate)
    }
  }, [orderOn, startDepot, endDepot])

  useEffect(() => {
    const markersIds: number[] = checkMarkersInsidePolygons()
    if (!areEqual(checkedMarkerIds, markersIds)) {
      updateCheckedMarkers(markersIds)
    }

    if (buttonRefs.current?.clearPolygons?.style) {
      buttonRefs.current.clearPolygons.style.display = polygons.length ? "block" : "none"
    }
  }, [polygons, checkedMarkerIds, checkMarkersInsidePolygons, updateCheckedMarkers])

  const { clear: clearDraw } = usePolygonDraw({
    map: mapInstance,
    mode: mode === MapModeOption.Polygon ? "polygon" : "hand",
    onPolygonsChange: setPolygons,
  })

  const clearPolygons = useCallback(() => {
    clearDraw()
  }, [clearDraw])

  const handleToggle = (event:any) => {
    if(setOrderOn){
      setOrderOn(event.target.checked ||  false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid sx={{ ml: "1em", mt: "1em" }} xs={12} spacing={2} container>
          <Grid item xs={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={orderOn}
                  onChange={handleToggle}
                  color="primary"
                />
              }
              label={t("collection_groups.mutate_page.set_order")}
            />
          </Grid>
          <Grid item xs={9}>
            {orderOn && <Grid container xs={12}>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  fullWidth
                  label={t("collection_groups.mutate_page.start_depot")}
                  value={startDepot}
                  onChange={(e) => setStartDepot(e.target.value)}
                >
                  <MenuItem value="">{t("collection_groups.mutate_page.not_set")}</MenuItem>
                  {departurePoints?.map((dp) => (
                    <MenuItem key={dp.id} value={dp.id}>
                      {dp.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={5} sx={{ ml: "1em", mr: "1em" }}>
                <TextField
                  select
                  fullWidth
                  label={t("collection_groups.mutate_page.end_depot")}
                  value={endDepot}
                  onChange={(e) => setEndDepot(e.target.value)}
                >
                  <MenuItem value="">{t("collection_groups.mutate_page.not_set")}</MenuItem>
                  {departurePoints?.map((dp) => (
                    <MenuItem key={dp.id} value={dp.id}>
                      {dp.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>}
          </Grid>

      </Grid>
      <Grid id="table-grid-item" item xs={8}>
        <Stack direction="column" spacing={2}>
          {!orderOn && <CollectionGroupCollectionPointsFilter /> }
          <CollectionGroupCollectionPointsTableDragNDrop />
        </Stack>
      </Grid>
      <Grid id="map-grid-item" item xs={4}>
        {!loading && (
          <CustomMap
            key={+(mapUpdate || 0)+"map"}
            height={`${mapHeight}px`}
            markers={markers}
            doRenderCustomInfoWindow
            renderCustomInfoWindow={renderCustomInfoWindow}
            disableFitBounds
            disableMultipleInfoWindows
            onInit={fitMapBounds}
          />
        )}
        {loading && <Skeleton variant="rectangular" height={600} />}
      </Grid>
    </Grid>
  )
}
